import Vue from 'vue';
import { GetterTree } from 'vuex';
import { PasswordState, RootState } from '@/store/types';
import { validationMixin } from 'vuelidate';
import { required, minLength, sameAs, not } from 'vuelidate/lib/validators';
import {
  atLeastOneNumber,
  atLeastOneLowercase,
  atLeastOneUppercase,
} from '@/store/validators';
import { PasswordValidationErrors, passwordSuccess } from './types';
import { Password } from '@/models/password';

const getters: GetterTree<PasswordState, RootState> = {
  password(state: PasswordState): Password {
    return {
      currentPassword: state.password,
      newPassword: state.newPassword,
    };
  },

  validator(state: PasswordState): Vue {
    return new Vue({
      mixins: [validationMixin],
      computed: {
        password: () => state.password,
        newPassword: () => state.newPassword,
        confirmNewPassword: () => state.confirmNewPassword,
      },
      validations: {
        password: {
          required,
          minLength: minLength(6),
          atLeastOneNumber,
          atLeastOneLowercase,
          atLeastOneUppercase,
        },
        newPassword: {
          required,
          minLength: minLength(6),
          atLeastOneNumber,
          atLeastOneLowercase,
          atLeastOneUppercase,
          sameAs: not(sameAs('password')),
        },
        confirmNewPassword: {
          required,
          minLength: minLength(6),
          atLeastOneNumber,
          atLeastOneLowercase,
          atLeastOneUppercase,
          sameAs: sameAs('newPassword'),
        },
      },
    });
  },

  errors(state: PasswordState, getters): PasswordValidationErrors {
    const errors: PasswordValidationErrors = {
      password: [],
      newPassword: [],
      confirmNewPassword: [],
    };

    /** Password */
    !getters.validator.$v.password.required &&
      errors.password.push('The password is required.');

    !getters.validator.$v.password.atLeastOneLowercase &&
      errors.password.push(
        'The password must have at least one lowercase letter.'
      );

    !getters.validator.$v.password.atLeastOneUppercase &&
      errors.password.push('The password must be at least one capital letter.');

    !getters.validator.$v.password.atLeastOneNumber &&
      errors.password.push('The password must have at least one number.');

    !getters.validator.$v.password.minLength &&
      errors.password.push('The password must be at least 6 characters long.');

    // /** New Password */
    !getters.validator.$v.newPassword.required &&
      errors.newPassword.push('The new password is required.');

    !getters.validator.$v.newPassword.atLeastOneLowercase &&
      errors.newPassword.push(
        'The new password must have at least one lowercase letter.'
      );

    !getters.validator.$v.newPassword.atLeastOneUppercase &&
      errors.newPassword.push(
        'The new password must have at least one capital letter.    '
      );

    !getters.validator.$v.newPassword.atLeastOneNumber &&
      errors.newPassword.push(
        'The new password must have at least one number.'
      );

    !getters.validator.$v.newPassword.minLength &&
      errors.newPassword.push(
        'The new password must be at least 6 characters long.'
      );

    !getters.validator.$v.newPassword.sameAs &&
      errors.newPassword.push(
        'The new password must be different from the previous one.'
      );

    // /** Confirm New Password */
    !getters.validator.$v.confirmNewPassword.required &&
      errors.confirmNewPassword.push(
        'Confirmation of the new password is required.'
      );

    !getters.validator.$v.confirmNewPassword.atLeastOneLowercase &&
      errors.confirmNewPassword.push(
        'Confirmation of the new password must have at least one lowercase letter.'
      );

    !getters.validator.$v.confirmNewPassword.atLeastOneUppercase &&
      errors.confirmNewPassword.push(
        'Confirmation of the new password must have at least one capital letter.'
      );

    !getters.validator.$v.confirmNewPassword.atLeastOneNumber &&
      errors.confirmNewPassword.push(
        'Confirmation of the new password must have at least one number.'
      );

    !getters.validator.$v.confirmNewPassword.minLength &&
      errors.confirmNewPassword.push(
        'Confirmation of the new password must be at least 6 characters long.'
      );

    !getters.validator.$v.confirmNewPassword.sameAs &&
      errors.confirmNewPassword.push(
        'The new password and confirmation must be the same.'
      );

    return errors;
  },
  success(state: PasswordState, getters): passwordSuccess {
    const formSuccess: passwordSuccess = {
      password: '',
      newPassword: '',
      confirmNewPassword: '',
    };

    !getters.validator.$v.password.$invalid &&
      (formSuccess.password = 'The password is valid.');

    !getters.validator.$v.newPassword.$invalid &&
      (formSuccess.newPassword = 'The new password is valid.');

    !getters.validator.$v.confirmNewPassword.$invalid &&
      (formSuccess.confirmNewPassword =
        'The confirmation of the new password is valid.');

    return formSuccess;
  },

  valid(state: PasswordState, getters) {
    return !getters.validator.$v.$invalid;
  },
};

export default getters;
